import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { Formik } from 'formik'
import React from 'react'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../../../app/hooks/useLoggedInUser'
import { useNotificationStack } from '../../../../app/hooks/useNotificationStack'
import {
  useAddContactTypeMutation,
  useGetContactCategoriesByCustomerIdQuery,
  useGetContactCategoriesByTenantIdQuery,
  useUpdateContactTypeMutation,
} from '../../../../app/redux-fetch/apiCoreQuery'
import { IDropDownItem } from '../../../../app/types'
import FormDropdown from '../../../../components/FormDropDown/FormDropdown'
import commonMessages from '../../../../components/Messages/commonMessages'
import RequiredAsterisk from '../../../../components/RequiredAsterisk/RequiredAsterisk'
import SwitchWithLabels from '../../../../components/Switches/SwitchWithLabels'
import { SectionLabelTypography } from '../../../../components/Typographies/styles'
import { IContactType } from '../../../Contacts/types'
import messages from '../messages'
import { contactTypeValidationSchema } from './contactTypeValidationSchema'

type ContactTypeFormProps = {
  contactType: IContactType
  handleClose: () => void
  isDisabled?: boolean
}

const ContactTypeForm: React.FC<ContactTypeFormProps> = ({ contactType, handleClose, isDisabled }) => {
  const { formatMessage } = useIntl()
  const isNew = !contactType?.id

  const [addContactType] = useAddContactTypeMutation()
  const [updateContactType] = useUpdateContactTypeMutation()

  const { tenantId, customerId } = useLoggedInUser()

  const { data: contactCategories = [] } = customerId
    ? useGetContactCategoriesByCustomerIdQuery(customerId)
    : useGetContactCategoriesByTenantIdQuery(tenantId)

  const { enqueueSuccess, enqueueFailure } = useNotificationStack()

  const onSubmit = async (data: IContactType) => {
    try {
      if (isNew) {
        await addContactType(data).unwrap()
        enqueueSuccess(messages.contactTypeCreated)
      } else {
        await updateContactType(data).unwrap()
        enqueueSuccess(messages.contactTypeUpdated)
      }
    } catch (error) {
      enqueueFailure(isNew ? messages.contactTypeNotCreated : messages.contactTypeNotUpdated)
    } finally {
      handleClose()
    }
  }

  return (
    <Formik
      initialValues={{...contactType, enabled: contactType.enabled ?? true}}
      validationSchema={contactTypeValidationSchema}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, handleChange, handleSubmit, isSubmitting, setFieldValue }) => (
        <form onSubmit={handleSubmit}>
          <Box sx={{ minWidth: '450px', m: '32px', pt: '48px' }}>
            <Box sx={{ mb: 2 }}>
              <SectionLabelTypography variant='subtitle1' sx={{ mb: 1 }}>
                {formatMessage(messages.contactTypeName)}
                <RequiredAsterisk />
              </SectionLabelTypography>
              <TextField
                fullWidth
                id='name'
                name='name'
                value={values.name || ''}
                inputProps={{ maxLength: 50 }}
                onChange={handleChange}
                helperText={touched.name && errors.name}
                error={touched.name && Boolean(errors.name)}
                disabled={isDisabled}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <SectionLabelTypography variant='subtitle1' sx={{ mb: 1 }}>
                {formatMessage(messages.contactCategory)}
                <RequiredAsterisk />
              </SectionLabelTypography>
              <FormDropdown
                id='contactCategoryId'
                items={contactCategories as IDropDownItem[]}
                disabled={isDisabled || !isNew}
                onChange={(_e: any, name: string, value: number) => setFieldValue(name, value)}
                value={values.contactCategoryId}
                error={touched.contactCategoryId && Boolean(errors.contactCategoryId)}
                errorText={touched.contactCategoryId && errors.contactCategoryId}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <SwitchWithLabels
                value={values.enabled}
                onChange={(value) => setFieldValue('enabled', value)}
                labelAfter={formatMessage(messages.enabled)}
                disabled={isDisabled}
              />
            </Box>
          </Box>
          {!isDisabled && (
            <Box
              sx={{
                position: 'fixed',
                bottom: 0,
                width: '100%',
                padding: '16px',
                backgroundColor: '#fff',
                borderTop: '1px solid #e0e0e0',
                zIndex: 999,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button color='primary' variant='outlined' onClick={handleClose}>
                  {formatMessage(commonMessages.cancelBtn)}
                </Button>
                <Button color='primary' variant='contained' type='submit' disabled={isSubmitting}>
                  {isNew
                    ? formatMessage(commonMessages.createBtn)
                    : formatMessage(commonMessages.updateBtn)}
                </Button>
              </Box>
            </Box>
          )}
        </form>
      )}
    </Formik>
  )
}

export default ContactTypeForm
